<template>
  <div class="pb-20" v-if="gameParticipation">
    <div
      class="mb-6 flex flex-col items-center justify-center text-base text-center transition-all text-secondary"
      :class="{ 'fade-out': won, hidden: won }"
    >
      <div class="font-body">
        {{
          game?.type === "scratch"
            ? $t("scratch-explanation")
            : $t("spin-explanation")
        }}:
      </div>
    </div>
    <scratch-game
      v-if="game?.type === 'scratch'"
      :promotionsById="promotionsById"
      :gameParticipation="gameParticipation"
      @scratchFinished="won = true"
    />
    <spin-wheel-game
      v-else
      :game="game"
      :gameParticipation="gameParticipation"
      :promotionsById="promotionsById"
      @spinFinished="won = true"
    />
    <div
      class="mt-3 flex flex-col items-center justify-center font-semibold text-xl heartbeat transition-all text-secondary"
      :class="{ 'fade-out': won, hidden: won }"
    >
      <c-icon name="KeyboardArrowUpRound" size="2rem" />
      <div class="-mt-2">
        {{
          game?.type === "scratch" ? $t("scratch-here") : $t("click-to-spin")
        }}
      </div>
    </div>
    <div
      id="wonSection"
      :class="{ 'fade-in': won, hidden: !won }"
      class="text-secondary flex flex-col items-center transition-all text-xl text-center mt-12 pb-12"
    >
      <div v-if="gameParticipation.couponPromotionId">
        <div>
          {{ $t("congratulations") }}
        </div>
        <div class="font-semibold mt-1">
          {{
            $t("coupon-won-message", {
              couponPromotionName:
                promotionsById?.[gameParticipation?.couponPromotionId]?.name,
            })
          }}
        </div>
        <div class="text-base mt-10">
          {{
            $t("download-pass-good-message", {
              organizationName: organization.name,
            })
          }}:
        </div>
      </div>
      <div v-else>
        <div>
          {{ $t("too-bad") }}
        </div>
        <div class="text-base mt-10">
          {{
            $t("download-pass-bad-message", {
              organizationName: organization.name,
            })
          }}
        </div>
      </div>
      <div v-if="lastVisitWithoutRating" class="mt-4">
        {{ $t("ask-review-message") }}:
        <div
          class="flex flex-row items-center justify-center mt-6 text-[3.5rem]"
        >
          <div v-for="i in 5" :key="i" @click="handleRating(i)">☆</div>
        </div>
      </div>
      <download-buttons v-else />
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";
import { authStore } from "../stores/auth";
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import DownloadButtons from "./DownloadButtons.vue";
import ScratchGame from "./ScratchGame.vue";
import SpinWheelGame from "./SpinWheelGame.vue";
import api from "../api";
import { nextTick } from "vue";
import prizeAchievedSound from "@/assets/prize-achieved.mp3";
import moment from "moment";

function scrollToElement(element, duration = 2000) {
  const start = window.scrollY;
  const end = element.getBoundingClientRect().top + window.scrollY;
  const startTime = performance.now();

  function scrollStep(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo(0, start + (end - start) * progress);

    if (progress < 1) {
      requestAnimationFrame(scrollStep);
    }
  }

  requestAnimationFrame(scrollStep);
}

export default {
  name: "GamePage",
  data() {
    return {
      won: false,
      promotions: [],
      gameParticipation: null,
      game: null,
      lastVisitWithoutRating: null,
    };
  },
  async mounted() {
    this.promotions = (
      await api.get(`/organization/${this.branding.organizationId}/promotions`)
    ).data.promotions;
    let gameData = (
      await api.get(`/gameParticipation/${this.gameParticipationId}`)
    ).data;
    await new Promise((resolve) => {
      let script = document.createElement("script");
      script.src = "https://code.createjs.com/1.0.0/soundjs.min.js";
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        resolve();
      };
    });
    createjs.Sound.registerSound({
      src: prizeAchievedSound,
      id: "soundAchieved",
    });
    this.gameParticipation = gameData.gameParticipation;
    this.game = gameData.game;
    nextTick(() => {
      if (
        this.gameParticipation.playedTime &&
        this.gameParticipation.id !== "1234" &&
        this.organization.id !== "b7318228-9de1-424e-9258-3fcb09e126f3" // demo organization
      ) {
        this.won = true;
      } else if (!this.customerId) {
        this.$router.replace({ name: "customerHub" });
        return;
      }
    });
  },
  methods: {
    handleRating(rating) {
      this.$router.push({
        name: "ratingRedirect",
        query: {
          visitId: this.lastVisitWithoutRating.id,
          rate: rating,
        },
      });
    },
    async handleWon() {
      if (this.gameParticipation.couponPromotionId) {
        this.showConfetti(1);
        this.showConfetti(-1);

        createjs.Sound.volume = 0.4;
        createjs.Sound.play("soundAchieved");
      }
      let wonSection = document.getElementById("wonSection");
      setTimeout(() => {
        // wonSection.scrollIntoView({ behavior: "smooth", block: "center" });
        scrollToElement(wonSection, 2000);
      }, 100);
      await api.post(`/gameParticipation/${this.gameParticipationId}/played`);
      if (
        moment(this.currentCustomer.creationTime).isBefore(
          moment().subtract(1, "day")
        )
      ) {
        this.lastVisitWithoutRating = (
          await api.get(`/customer/${this.customerId}/lastVisitWithoutRating`)
        ).data?.lastVisitWithoutRating;
      }
    },
    showConfetti(side = 1) {
      function random(max) {
        return Math.random() * (max - 0) + 0;
      }

      var c = document.createDocumentFragment();
      for (var i = 0; i < 200; i++) {
        var styles =
          "transform: translate3d(" +
          random(1.2) * random(700 * side) +
          "px, " +
          random(1.2) * random(-1000) +
          "px, 0) rotate(" +
          random(360) +
          "deg);\
                  background: hsla(" +
          random(360) +
          ",100%,50%,1);\
                  animation: bang 4000ms ease-out forwards;\
                  opacity: 0";

        var e = document.createElement("i");
        e.style.cssText = styles.toString();
        c.appendChild(e);
      }
      let confettiOrigin = document.createElement("div");
      confettiOrigin.classList.add("confettiOrigin");
      confettiOrigin.classList.add("fixed");
      side === 1
        ? confettiOrigin.classList.add("left-0")
        : confettiOrigin.classList.add("right-0");
      confettiOrigin.classList.add("bottom-0");
      confettiOrigin.classList.add("w-2");
      document.body.appendChild(confettiOrigin);
      confettiOrigin.appendChild(c);
    },
  },
  computed: {
    ...mapState(authStore, [
      "branding",
      "organization",
      "customerId",
      "currentCustomer",
    ]),
    gameParticipationId() {
      return this.$route.params.gameParticipationId;
    },
    promotionsById() {
      return this.promotions.reduce((acc, promotion) => {
        acc[promotion.id] = promotion;
        return acc;
      }, {});
    },
  },
  components: {
    CIcon,
    DownloadButtons,
    ScratchGame,
    SpinWheelGame,
  },
  watch: {
    won(val) {
      if (val) {
        this.handleWon();
      }
    },
  },
};
</script>

<style lang="scss">
.sc__inner {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.sc__wrapper {
  display: block;
  width: 100%;
  height: 250px;
  max-width: 250px;
  margin: 0 auto;
  border: 5px solid white;
}

.sc__container {
  position: relative;
  overflow: hidden;
  height: 300px;
  max-width: 300px;
}

.sc__container > img {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
}

.sc__container canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
}

@keyframes bang {
  from {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
}

.confettiOrigin {
  width: 240px;
  margin: 150px auto 0;
  text-align: center;
  padding: 10px 0;
  cursor: pointer;
  position: relative;
  span {
    color: #333;
    font-size: 0.9em;
  }
  i {
    position: absolute;
    display: block;
    left: 50%;
    top: 0;
    width: 5px;
    height: 15px;
    background: red;
    opacity: 0;
  }
}

.heartbeat {
  -webkit-animation: heartbeat 1s ease-in-out infinite both;
  animation: heartbeat 1s ease-in-out infinite both;
}

@-webkit-keyframes heartbeat {
  from {
    -webkit-transform: scale(1);
    transform: scale(1);
    -webkit-transform-origin: center center;
    transform-origin: center center;
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }
  10% {
    -webkit-transform: scale(0.91);
    transform: scale(0.91);
    -webkit-animation-timing-function: ease-in;
    animation-timing-function: ease-in;
  }
  17% {
    -webkit-transform: scale(0.98);
    transform: scale(0.98);
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }
  33% {
    -webkit-transform: scale(0.87);
    transform: scale(0.87);
    -webkit-animation-timing-function: ease-in;
    animation-timing-function: ease-in;
  }
  45% {
    -webkit-transform: scale(1);
    transform: scale(1);
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }
}
@keyframes heartbeat {
  from {
    -webkit-transform: scale(1);
    transform: scale(1);
    -webkit-transform-origin: center center;
    transform-origin: center center;
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }
  10% {
    -webkit-transform: scale(0.91);
    transform: scale(0.91);
    -webkit-animation-timing-function: ease-in;
    animation-timing-function: ease-in;
  }
  17% {
    -webkit-transform: scale(0.98);
    transform: scale(0.98);
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }
  33% {
    -webkit-transform: scale(0.87);
    transform: scale(0.87);
    -webkit-animation-timing-function: ease-in;
    animation-timing-function: ease-in;
  }
  45% {
    -webkit-transform: scale(1);
    transform: scale(1);
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }
}

.fade-out {
  -webkit-animation: fade-out 1.2s ease-out both;
  animation: fade-out 1.2s ease-out both;
}

@-webkit-keyframes fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    display: none;
  }
}
@keyframes fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    display: none;
  }
}

.fade-in {
  -webkit-animation: fade-in 1.2s cubic-bezier(0.39, 0.575, 0.565, 1) both;
  animation: fade-in 1.2s cubic-bezier(0.39, 0.575, 0.565, 1) both;
}

@-webkit-keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
